import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import Highcharts from "highcharts";
import { AdminHighcharts } from "./chartTheme";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import { openReportLoadingTab } from "../utils/reportPdfLoader";
import {
  capturePdfChartSection,
  PDF_CAPTURE_SCALE,
} from "./pdfSectionCapture";
import { buildInsightsKpiItems } from "./insightsKpiItems";
import { useTheme } from "../context/ThemeContext";
import {
  fetchInsights,
  fetchInsightsScores,
  fetchInsightsNarrative,
  fetchInsightsAnomalies,
  exportPortfolioRollupPdf,
} from "../api/analyticsClient";
import { isAbortError } from "../utils/fetchAbort";
import ChartExplainAction from "./ChartExplainAction";
import { buildInsightsChartExplainContexts, formatChartExplanation } from "./chartExplainBuilders";
import { isAdminLoggedIn } from "../api/adminClient";
import "./survey.css";
import {
  WORKSPACE_INDUSTRIES,
  WORKSPACE_COMPANY_SIZES,
  FILTER_ALL_INDUSTRIES,
  FILTER_ALL_COMPANY_SIZES,
} from "./companyWorkspaceOptions";
import {
  PILLAR_KEYS_FOR_CHARTS,
  getActivePillarKeys,
  getPillarScore,
  getPillarImportance,
  getPillarAgreement,
  hasAssessmentStructureMismatch,
  orgHasImportanceScores,
  orgHasAgreementScores,
} from "./companyInsightsConstants";
import AiSummaryCard from "./AiSummaryCard";
import BoardBriefCard from "./BoardBriefCard";

function median(values) {
  const v = [...values].sort((a, b) => a - b);
  if (!v.length) return null;
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}

function avg(nums) {
  const n = nums.filter((x) => x != null && !Number.isNaN(x));
  if (!n.length) return null;
  return Math.round(n.reduce((a, b) => a + b, 0) / n.length);
}

function buildNarrativeComparisonEntry(org, workspacePercentileById, cohortMedians, activePillarKeysArg) {
  // activePillarKeysArg falls back to PILLAR_KEYS_FOR_CHARTS for 5X workspaces
  const pillarKeys = activePillarKeysArg || PILLAR_KEYS_FOR_CHARTS;
  const pillars = pillarKeys.map(({ key, label }) => ({
    key,
    label,
    value: getPillarScore(org, key),
  })).filter((p) => p.value != null && !Number.isNaN(p.value));
  const byScore = [...pillars].sort((a, b) => b.value - a.value);
  const completionPct = org.invited
    ? Math.round((org.completed / org.invited) * 100)
    : null;
  const vsCohortOverall =
    org.overall != null && cohortMedians.overall != null
      ? Math.round(org.overall - cohortMedians.overall)
      : null;
  const pillarVsCohort = {};
  pillarKeys.forEach(({ key }) => {
    const val = getPillarScore(org, key);
    if (val != null && cohortMedians[key] != null) {
      pillarVsCohort[key] = Math.round(val - cohortMedians[key]);
    }
  });
  return {
    name: org.name,
    industry: org.industry,
    companySize: org.companySize,
    invited: org.invited,
    completed: org.completed,
    overall: org.overall,
    reportReady: org.reportReady,
    completionPct,
    workspacePercentile: workspacePercentileById.get(org.id) ?? null,
    vsCohortOverall,
    pillarVsCohort,
    strongestPillar: byScore[0]
      ? { label: byScore[0].label, value: Math.round(byScore[0].value) }
      : null,
    weakestPillar: byScore.length
      ? {
          label: byScore[byScore.length - 1].label,
          value: Math.round(byScore[byScore.length - 1].value),
        }
      : null,
    pillars: Object.fromEntries(pillars.map((p) => [p.key, Math.round(p.value)])),
  };
}

function inDateRange(isoDate, from, to) {
  if (!from && !to) return true;
  const t = new Date(isoDate).getTime();
  if (from && t < new Date(from).getTime()) return false;
  if (to && t > new Date(to).setHours(23, 59, 59, 999)) return false;
  return true;
}

/** Per-org weekly series from API weekly trend buckets; zeros when trend is absent. */
function weeklyCompletionsSeriesForOrg(org, weeklyTrend) {
  if (weeklyTrend?.completionsByOrgId && weeklyTrend.categories?.length) {
    const row = weeklyTrend.completionsByOrgId[org.id];
    if (!row) return weeklyTrend.categories.map(() => 0);
    return weeklyTrend.categories.map((_, i) => Math.max(0, Math.round(row[i] ?? 0)));
  }
  if (weeklyTrend?.categories?.length) {
    return weeklyTrend.categories.map(() => 0);
  }
  return [];
}

/** Per-org weekly LITE completion series from API buckets. */
function weeklyLiteCompletionsSeriesForOrg(org, weeklyTrend) {
  if (weeklyTrend?.liteCompletionsByOrgId && weeklyTrend.categories?.length) {
    const row = weeklyTrend.liteCompletionsByOrgId[org.id];
    if (!row) return weeklyTrend.categories.map(() => 0);
    return weeklyTrend.categories.map((_, i) => Math.max(0, Math.round(row[i] ?? 0)));
  }
  if (weeklyTrend?.categories?.length) {
    return weeklyTrend.categories.map(() => 0);
  }
  return [];
}

/** Portfolio / scope aggregate weekly LITE completions from API buckets. */
function weeklyTrendAggregateLiteCompletions(orgs, weeklyTrend) {
  if (weeklyTrend?.liteCompletionsByOrgId && weeklyTrend.categories?.length) {
    const n = weeklyTrend.categories.length;
    const sum = Array(n).fill(0);
    for (const o of orgs) {
      const row = weeklyTrend.liteCompletionsByOrgId[o.id];
      if (!row) continue;
      for (let i = 0; i < n; i++) sum[i] += row[i] ?? 0;
    }
    return sum.map((x) => Math.max(0, Math.round(x)));
  }
  if (weeklyTrend?.categories?.length) {
    return weeklyTrend.categories.map(() => 0);
  }
  return [];
}

/** Portfolio / scope aggregate weekly completions from API buckets. */
function weeklyTrendAggregateCompletions(orgs, weeklyTrend) {
  if (weeklyTrend?.completionsByOrgId && weeklyTrend.categories?.length) {
    const n = weeklyTrend.categories.length;
    const sum = Array(n).fill(0);
    for (const o of orgs) {
      const row = weeklyTrend.completionsByOrgId[o.id];
      if (!row) continue;
      for (let i = 0; i < n; i++) sum[i] += row[i] ?? 0;
    }
    return sum.map((x) => Math.max(0, Math.round(x)));
  }
  if (weeklyTrend?.categories?.length) {
    return weeklyTrend.categories.map(() => 0);
  }
  return [];
}

const selectClass =
  "admin-filter-select appearance-none bg-white border border-slate-200 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand block w-full min-w-0 max-w-full px-3 py-2.5 transition-colors hover:border-slate-300 shadow-sm";

/** Master table headings: capitalize each word (e.g. "Report ready" → "Report Ready"). */
function titleCaseHeading(label) {
  return String(label)
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/** Master table size cells: drop trailing "employees" from workspace labels. */
function formatCompanySizeForTable(size) {
  const s = String(size || "").trim();
  if (!s) return "—";
  return s.replace(/\s+employees\s*$/i, "").trim() || "—";
}

/** Column width from header text (~8px/char at 12px bold + cell padding). */
function tableColWidth(label, minPx = 72) {
  return `${Math.max(minPx, Math.ceil(String(label).length * 8) + 22)}px`;
}

/** Column width from header + longest cell value (e.g. full company names). */
function tableColWidthForContent(headerLabel, cellValues, minPx = 120) {
  const headerLen = String(headerLabel).length;
  const maxValLen = cellValues.reduce(
    (max, v) => Math.max(max, String(v ?? "").length),
    0
  );
  const chars = Math.max(headerLen, maxValLen);
  return `${Math.max(minPx, Math.ceil(chars * 7.2) + 18)}px`;
}

function getMasterTableCustomStyles(dark) {
  const headBg = dark ? "rgba(255, 255, 255, 0.06)" : "#f8fafc";
  const headColor = dark ? "#cbd5e1" : "#475569";
  const rowColor = dark ? "#e2e8f0" : "#0f172a";
  const cellColor = dark ? "#e2e8f0" : "#1e293b";
  const borderColor = dark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0";
  const rowBorder = dark ? "rgba(255, 255, 255, 0.06)" : "#f1f5f9";

  return {
    table: {
      style: {
        minWidth: "max-content",
        borderCollapse: "separate",
        borderSpacing: 0,
      },
    },
    headRow: {
      style: {
        minHeight: "44px",
        backgroundColor: headBg,
        borderBottomWidth: "1px",
        borderBottomColor: borderColor,
      },
    },
    headCells: {
      style: {
        fontSize: "11px",
        fontWeight: 700,
        color: headColor,
        background: headBg,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        whiteSpace: "normal",
        lineHeight: "1.3",
        overflow: "visible",
        textOverflow: "clip",
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
    rows: {
      style: {
        minHeight: "40px",
        color: rowColor,
        borderBottomWidth: "1px",
        borderBottomColor: rowBorder,
        transition: "background-color 0.12s ease",
      },
      highlightOnHoverStyle: {
        backgroundColor: dark ? "rgba(34, 211, 238, 0.08)" : "rgba(0, 79, 115, 0.04)",
        cursor: "pointer",
        transition: "background-color 0.12s ease",
      },
    },
    cells: {
      style: {
        fontSize: "12.5px",
        color: cellColor,
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
    pagination: {
      style: {
        borderTopWidth: "1px",
        borderTopColor: borderColor,
        fontSize: "13px",
        color: headColor,
        backgroundColor: dark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
      },
      pageButtonsStyle: {
        borderRadius: "8px",
        color: dark ? "#67e8f9" : "#004f73",
        fill: dark ? "#67e8f9" : "#004f73",
      },
    },
  };
}

/** Keeps charts inside flex layouts; width null = follow container */
const CHART_FLUID = {
  reflow: true,
  width: null,
  style: { maxWidth: "100%" },
  spacingLeft: 2,
  spacingRight: 2,
  spacingTop: 6,
  spacingBottom: 4,
};

const hcContainerProps = {
  className: "w-full max-w-full",
  style: { maxWidth: "100%" },
};

function ChartPanel({ children, scrollY }) {
  return (
    <div
      className={
        scrollY
          ? "w-full min-w-0 max-w-full overflow-x-auto max-h-[min(65vh,480px)] overflow-y-auto overscroll-contain"
          : "w-full min-w-0 max-w-full overflow-x-auto"
      }
    >
      {children}
    </div>
  );
}

const PILLAR_SHORT_NAMES = {
  strategic: "Strategic",
  scalable: "Scalable",
  relentless: "Relentless",
  disciplined: "Disciplined",
  energized: "Energized",
  financial: "Financial",
};

/**
 * Organisation line/bar colours — chosen to avoid cohort benchmark grey and participant-stack blues.
 * Same org id always maps to the same colour (compare order first, then focus, then remaining orgs by id).
 */
const ORG_PALETTE = [
  "#7c3aed",
  "#ea580c",
  "#059669",
  "#dc2626",
  "#0891b2",
  "#ca8a04",
  "#db2777",
  "#4f46e5",
];
/** Column benchmark — not an org; distinct from ORG_PALETTE */
const COHORT_MEDIAN_COLOR = "#64748b";
const NEUTRAL_ORG_COLOR = "#cbd5e1";

const PARTICIPANT_COMPLETED = "#004f73";
const PARTICIPANT_LITE_COMPLETED = "#3b82f6";
const PARTICIPANT_PENDING = "#38bdf8";
const PARTICIPANT_NOT_STARTED = "#94a3b8";

function buildOrgColorMap(compareIds, focusOrgId, remainingSortedOrgIds) {
  const map = new Map();
  let i = 0;
  compareIds.forEach((id) => {
    if (!map.has(id)) {
      map.set(id, ORG_PALETTE[i % ORG_PALETTE.length]);
      i += 1;
    }
  });
  if (focusOrgId && !map.has(focusOrgId)) {
    map.set(focusOrgId, ORG_PALETTE[i % ORG_PALETTE.length]);
    i += 1;
  }
  remainingSortedOrgIds.forEach((id) => {
    if (!map.has(id)) {
      map.set(id, ORG_PALETTE[i % ORG_PALETTE.length]);
      i += 1;
    }
  });
  return map;
}

function shortOrgLabel(name, max = 12) {
  if (!name) return "—";
  return name.length > max ? `${name.slice(0, max - 1)}…` : name;
}

function sortCompaniesByOverallDesc(orgs) {
  return [...orgs].sort((a, b) => {
    const ao = a.overall ?? -Infinity;
    const bo = b.overall ?? -Infinity;
    return bo - ao;
  });
}

/** Overall percentile vs full workspace report-ready cohort; tied scores share one percentile. */
function buildWorkspacePercentileByOrgId(reportReadyOrgs) {
  const sorted = [...reportReadyOrgs].sort(
    (a, b) => a.overall - b.overall || String(a.name).localeCompare(String(b.name))
  );
  const n = sorted.length;
  const map = new Map();
  if (n === 0) return map;
  if (n === 1) {
    map.set(sorted[0].id, 100);
    return map;
  }
  let i = 0;
  while (i < n) {
    let j = i;
    while (j + 1 < n && sorted[j + 1].overall === sorted[i].overall) {
      j += 1;
    }
    const pct = Math.round(((i + j) / 2 / (n - 1)) * 100);
    for (let k = i; k <= j; k += 1) {
      map.set(sorted[k].id, pct);
    }
    i = j + 1;
  }
  return map;
}

/** Category-axis labels: show full text without Highcharts ellipsis/crop. */
function fullCategoryAxisLabels(overrides = {}) {
  return {
    crop: false,
    overflow: "allow",
    style: { fontSize: "9px", textOverflow: "none", whiteSpace: "nowrap" },
    reserveSpace: true,
    ...overrides,
  };
}

/** Approximate pixel width for 9px category labels (used with reserveSpace, not extra chart margin). */
function categoryLabelWidthPx(names, cap = 200) {
  const maxLen = names.reduce((m, n) => Math.max(m, String(n || "").length), 0);
  return Math.min(cap, Math.max(48, Math.ceil(maxLen * 5.2)));
}

/** Scrollable HTML tooltip shell for Insights Highcharts (large company lists, compare lines, etc.). */
function wrapScrollableTooltipHtml(innerHtml) {
  return `<div class="insights-tooltip-scroll">${innerHtml}</div>`;
}

const INSIGHTS_SCROLLABLE_TOOLTIP = {
  useHTML: true,
  outside: true,
  stickOnContact: true,
  padding: 8,
  style: { pointerEvents: "auto" },
};

function scrollableTooltipOptions(formatter) {
  return {
    ...INSIGHTS_SCROLLABLE_TOOLTIP,
    formatter: function () {
      return wrapScrollableTooltipHtml(formatter.call(this));
    },
  };
}

/** Stacked column chart tooltip: full company name; HTML for outside positioning. */
function stackedColumnTooltipByOrgIndex(orgs) {
  return function () {
    const idx = this.points?.[0]?.point?.index ?? this.point?.index;
    const org = typeof idx === "number" ? orgs[idx] : null;
    const header = `<b>${org?.name ?? this.x}</b><br/>`;
    if (this.points?.length) {
      return wrapScrollableTooltipHtml(
        header +
          this.points
            .map(
              (p) =>
                `<span style="color:${p.color}">\u25CF</span> ${p.series.name}: <b>${p.y}</b><br/>`
            )
            .join("")
      );
    }
    return wrapScrollableTooltipHtml(`${header}${this.series.name}: <b>${this.y}</b>`);
  };
}

function participationColumnTooltip(orgs) {
  return {
    ...INSIGHTS_SCROLLABLE_TOOLTIP,
    shared: true,
    formatter: stackedColumnTooltipByOrgIndex(orgs),
  };
}

/** Week label for tooltips — Highcharts may expose index instead of category after option updates. */
function weekTooltipLabel(categories, point, x) {
  if (!categories?.length) return String(x ?? "—");
  if (point?.category != null && String(point.category) !== String(point.index)) {
    return String(point.category);
  }
  const idx =
    point?.index ??
    (typeof x === "number" && Number.isInteger(x) ? x : categories.indexOf(String(x)));
  if (Number.isInteger(idx) && idx >= 0 && idx < categories.length) {
    return categories[idx];
  }
  if (typeof x === "string" && x) return x;
  return String(x ?? "—");
}

function weeklyXAxis(categories) {
  return {
    type: "category",
    categories: categories ?? [],
    crosshair: true,
  };
}

/** Multi-series line chart: series names on chart may be short; tooltip uses full org names. */
function lineChartSharedTooltipBySeriesIndex(orgsBySeriesIndex, weekCategories) {
  return function () {
    const pt = this.points?.[0]?.point;
    const week = weekTooltipLabel(weekCategories, pt, this.x);
    let html = `<b>${week}</b><br/>`;
    this.points.forEach((p) => {
      const org = orgsBySeriesIndex[p.series.index];
      const label = org?.name ?? p.series.name;
      html += `<span style="color:${p.color}">\u25CF</span> ${label}: <b>${p.y}</b><br/>`;
    });
    return wrapScrollableTooltipHtml(html);
  };
}

function weeklySingleLineTooltip(weekCategories) {
  return function () {
    const week = weekTooltipLabel(weekCategories, this.point, this.x);
    return wrapScrollableTooltipHtml(`<b>${week}</b><br/><b>${this.y}</b> completions`);
  };
}

const MAX_ORGS_PER_PARTICIPATION_CHART = 12;
/** Max lines on weekly compare chart (compare UI allows up to 4) */
const MAX_WEEKLY_COMPARE_LINES = 8;
const MAX_COMPARE_ORGS = 4;
/** Default cohort view: cap overall score chart; percentiles always vs full workspace report-ready set. */
const MAX_OVERALL_RANK_CHART_ORGS = 10;

function formatNarrativeInline(text) {
  let key = 0;
  const parts = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let match;
  const source = String(text || "");
  while ((match = re.exec(source)) !== null) {
    if (match.index > last) parts.push(source.slice(last, match.index));
    parts.push(
      <strong key={key++} className="font-semibold text-gray-900">
        {match[1]}
      </strong>
    );
    last = match.index + match[0].length;
  }
  if (last < source.length) parts.push(source.slice(last));
  if (!parts.length) return source;
  return parts;
}

function formatNarrativeParagraph(text) {
  const source = String(text || "").trim();
  if (!source.includes("**")) {
    const colonMatch = source.match(/^([^:\n]{3,50}):\s([\s\S]+)$/);
    if (colonMatch) {
      return (
        <>
          <strong className="font-semibold text-gray-900">{colonMatch[1]}:</strong>{" "}
          {formatNarrativeInline(colonMatch[2])}
        </>
      );
    }
  }
  return formatNarrativeInline(source);
}

function isNarrativeFootnote(text) {
  const lower = String(text || "").trim().toLowerCase();
  return (
    lower.startsWith("adjust filters") ||
    lower.startsWith("change filters") ||
    lower.includes("charts and this summary update to match")
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="flex items-baseline gap-3 mb-3">
      <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 tracking-tight">{title}</h2>
      {subtitle && <span className="text-xs text-slate-400">{subtitle}</span>}
    </div>
  );
}

function KpiCardSkeleton() {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/80 shadow-sm p-4 animate-pulse">
      <div className="flex items-center justify-between mb-3">
        <div className="h-8 w-8 rounded-xl bg-slate-200 dark:bg-slate-700" />
      </div>
      <div className="h-7 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
      <div className="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded" />
    </div>
  );
}

function ChartCardSkeleton({ height = 280 }) {
  return (
    <div
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 animate-pulse"
      style={{ minHeight: height }}
    >
      <div className="h-4 w-40 bg-slate-200 rounded mb-4" />
      <div className="flex items-end gap-2 h-[calc(100%-2.5rem)]">
        {[60, 80, 45, 90, 55, 70, 40, 85].map((h, i) => (
          <div
            key={i}
            className="flex-1 bg-slate-100 rounded-t"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
  );
}

const KPI_META = {
  totalOrgs:            { icon: "building",    color: "text-brand",          bg: "bg-brand/8" },
  participantsInvited:  { icon: "users",        color: "text-violet-600",     bg: "bg-violet-50" },
  participantsCompleted:{ icon: "check-circle", color: "text-emerald-600",    bg: "bg-emerald-50" },
  participantsLiteCompleted:{ icon: "zap",      color: "text-blue-500",       bg: "bg-blue-50" },
  avgCompletion:        { icon: "pie",          color: "text-amber-600",      bg: "bg-amber-50" },
  avgOverall:           { icon: "star",         color: "text-brand",          bg: "bg-brand/8" },
  activeAssessments:    { icon: "activity",     color: "text-sky-600",        bg: "bg-sky-50" },
  reportReadyCount:     { icon: "report",       color: "text-teal-600",       bg: "bg-teal-50" },
};

function KpiIcon({ type }) {
  const cls = "w-4 h-4";
  if (type === "building") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
  if (type === "users") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
  if (type === "check-circle") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  if (type === "zap") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
  if (type === "pie") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
    </svg>
  );
  if (type === "star") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polygon strokeLinecap="round" strokeLinejoin="round" points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
  if (type === "activity") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline strokeLinecap="round" strokeLinejoin="round" points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
  if (type === "report") return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
  return null;
}

function KpiCard({ label, value, metaKey, loading }) {
  const meta = KPI_META[metaKey] || { icon: "activity", color: "text-brand", bg: "bg-brand/8" };
  if (loading) return <KpiCardSkeleton />;
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/80 shadow-sm p-4 hover:shadow-md hover:border-slate-200 dark:hover:border-slate-600 transition-all duration-200 group">
      <div className="flex items-center justify-between mb-3">
        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl ${meta.bg} ${meta.color}`}>
          <KpiIcon type={meta.icon} />
        </span>
      </div>
      <div className="text-[1.6rem] font-bold text-slate-900 dark:text-slate-100 tabular-nums leading-none mb-1.5">
        {value ?? "—"}
      </div>
      <div className="text-xs font-medium text-slate-500 dark:text-slate-400 leading-snug">{label}</div>
    </div>
  );
}

function ChevronDownIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function XSmallIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function DownloadIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function FilterIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  );
}

function InsightsNarrativePanel({ narrative, narrativeLoading, narrativeSource, onGenerate, dataReady }) {
  const filteredNarrative = useMemo(() => {
    if (!narrative) return "";
    return narrative
      .split(/\n\n+/)
      .filter(Boolean)
      .filter((para) => !isNarrativeFootnote(para))
      .join("\n\n");
  }, [narrative]);

  return (
    <AiSummaryCard
      narrative={filteredNarrative}
      narrativeLoading={narrativeLoading}
      narrativeSource={narrativeSource}
      onGenerate={onGenerate}
      dataReady={dataReady}
      renderParagraph={formatNarrativeParagraph}
      readDisabled={insightsLoadingGuard(narrative, narrativeLoading)}
      pdfNarrativeAttr
      emptyHint="Generate an AI summary from the loaded portfolio data."
      loadingHint="Summary available after insights data loads."
    />
  );
}

function insightsLoadingGuard(narrative, narrativeLoading) {
  return !narrative && narrativeLoading;
}

function mergeOrgScores(org, scores) {
  if (!scores) return org;
  return {
    ...org,
    reportReady: Boolean(scores.reportReady),
    overall: scores.overall ?? null,
    strategic: scores.strategic ?? null,
    scalable: scores.scalable ?? null,
    relentless: scores.relentless ?? null,
    disciplined: scores.disciplined ?? null,
    energized: scores.energized ?? null,
    financial: scores.financial ?? null,
    importanceByPillar: scores.importanceByPillar || org.importanceByPillar,
    agreementByPillar: scores.agreementByPillar || org.agreementByPillar,
    dimensions: scores.dimensions ?? null,
  };
}

const InsightsPage = ({ embedded = false }) => {
  const outletContext = useOutletContext();
  const setAdminAssistantContext = outletContext?.setAdminAssistantContext;
  const { theme } = useTheme();
  const dark = theme === "dark";
  const masterTableCustomStyles = useMemo(() => getMasterTableCustomStyles(dark), [dark]);
  const [organisations, setOrganisations] = useState([]);
  const [weeklyTrend, setWeeklyTrend] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [scoresLoading, setScoresLoading] = useState(false);
  const [usingLiveInsights, setUsingLiveInsights] = useState(false);
  /** Shown when the API is not called (missing URL/token) or configuration needs attention */
  const [insightsLoadHint, setInsightsLoadHint] = useState(null);
  const [unassignedSurveys, setUnassignedSurveys] = useState(null);

  const [industry, setIndustry] = useState(FILTER_ALL_INDUSTRIES);
  const [companySize, setCompanySize] = useState(FILTER_ALL_COMPANY_SIZES);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [focusOrgId, setFocusOrgId] = useState("");
  const [compareIds, setCompareIds] = useState([]);
  const [comparePickerOpen, setComparePickerOpen] = useState(false);
  const [compareDraftIds, setCompareDraftIds] = useState([]);
  const comparePickerRef = useRef(null);
  const pdfLayoutRootRef = useRef(null);
  const pdfExportBusyRef = useRef(false);
  const insightsFirstLoadDoneRef = useRef(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeSource, setNarrativeSource] = useState("");
  const [chartExplainById, setChartExplainById] = useState({});
  const [chartExplainLoadingById, setChartExplainLoadingById] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [anomalies, setAnomalies] = useState([]);
  const [anomaliesDismissed, setAnomaliesDismissed] = useState(false);
  const aiSummaryBusyRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCompareAppliedRef = useRef(false);
  const pendingPortfolioExportRef = useRef(searchParams.get("exportPortfolio") === "1");

  useEffect(() => {
    if (!isAdminLoggedIn()) {
      setOrganisations([]);
      setWeeklyTrend(null);
      setUsingLiveInsights(false);
      setInsightsLoadHint("Sign in so your session token is available for Insights.");
      setInsightsLoading(false);
      setScoresLoading(false);
      return undefined;
    }
    setInsightsLoadHint(null);
    const controller = new AbortController();
    const { signal } = controller;
    setInsightsLoading(true);
    setScoresLoading(true);
    (async () => {
      let insightsOk = false;
      try {
        const payload = await fetchInsights({ dateFrom, dateTo, includeScores: false }, { signal });
        if (signal.aborted) return;

        const orgs = payload?.data?.organizations;
        if (!Array.isArray(orgs)) {
          setUnassignedSurveys(null);
          toast.error(payload?.message || "Could not load Insights");
          return;
        }
        setOrganisations(orgs);
        setWeeklyTrend(payload.data.weeklyTrend ?? null);
        const u = payload.data?.unassignedSurveys;
        setUnassignedSurveys(
          u && typeof u.count === "number"
            ? {
                count: u.count,
                completed: Number(u.completed) || 0,
                liteCompleted: Number(u.liteCompleted) || 0,
                pending: Number(u.pending) || 0,
                notStarted: Number(u.notStarted) || 0,
              }
            : null
        );
        setUsingLiveInsights(true);
        setInsightsLoadHint(null);
        if (!insightsFirstLoadDoneRef.current) {
          setFocusOrgId("");
          setCompareIds([]);
          insightsFirstLoadDoneRef.current = true;
        }
        insightsOk = true;
      } catch (e) {
        if (isAbortError(e) || signal.aborted) return;
        setUnassignedSurveys(null);
        toast.error(e?.message || "Could not load Insights");
      } finally {
        if (!signal.aborted) setInsightsLoading(false);
      }

      if (!insightsOk || signal.aborted) {
        if (!signal.aborted) setScoresLoading(false);
        return;
      }

      try {
        const scoresRes = await fetchInsightsScores({ dateFrom, dateTo }, { signal });
        if (signal.aborted) return;
        const byId = scoresRes?.data?.scoresByOrgId || {};
        setOrganisations((current) => current.map((org) => mergeOrgScores(org, byId[org.id])));
      } catch (e) {
        if (isAbortError(e) || signal.aborted) return;
        toast.error(e?.message || "Scores could not be loaded. The company list is still available.");
      } finally {
        if (!signal.aborted) setScoresLoading(false);
      }
    })();
    return () => controller.abort();
  }, [dateFrom, dateTo]);

  const weekCategories = useMemo(() => {
    if (weeklyTrend?.categories?.length) return weeklyTrend.categories;
    return [];
  }, [weeklyTrend]);

  /** Canonical profile values plus any extra values present in loaded data (legacy DB strings). */
  const industryFilterOptions = useMemo(() => {
    const seen = new Set(WORKSPACE_INDUSTRIES);
    const legacy = [];
    for (const o of organisations) {
      const v = (o.industry || "").trim();
      if (!v || v === "—") continue;
      if (!seen.has(v)) {
        seen.add(v);
        legacy.push(v);
      }
    }
    legacy.sort((a, b) => a.localeCompare(b));
    return [FILTER_ALL_INDUSTRIES, ...WORKSPACE_INDUSTRIES, ...legacy];
  }, [organisations]);

  const companySizeFilterOptions = useMemo(() => {
    const seen = new Set(WORKSPACE_COMPANY_SIZES);
    const legacy = [];
    for (const o of organisations) {
      const v = (o.companySize || "").trim();
      if (!v || v === "—") continue;
      if (!seen.has(v)) {
        seen.add(v);
        legacy.push(v);
      }
    }
    legacy.sort((a, b) => a.localeCompare(b));
    return [FILTER_ALL_COMPANY_SIZES, ...WORKSPACE_COMPANY_SIZES, ...legacy];
  }, [organisations]);

  useEffect(() => {
    if (industry !== FILTER_ALL_INDUSTRIES && !industryFilterOptions.includes(industry)) {
      setIndustry(FILTER_ALL_INDUSTRIES);
    }
  }, [industry, industryFilterOptions]);

  useEffect(() => {
    if (
      companySize !== FILTER_ALL_COMPANY_SIZES &&
      !companySizeFilterOptions.includes(companySize)
    ) {
      setCompanySize(FILTER_ALL_COMPANY_SIZES);
    }
  }, [companySize, companySizeFilterOptions]);

  const filteredOrgs = useMemo(() => {
    return organisations.filter((o) => {
      if (industry !== FILTER_ALL_INDUSTRIES && o.industry !== industry) return false;
      if (companySize !== FILTER_ALL_COMPANY_SIZES && o.companySize !== companySize) return false;
      if (!inDateRange(o.lastActivity, dateFrom, dateTo)) return false;
      return true;
    });
  }, [organisations, industry, companySize, dateFrom, dateTo]);

  const masterTableRows = useMemo(
    () => sortCompaniesByOverallDesc(filteredOrgs),
    [filteredOrgs]
  );

  const reportReadyOrgs = useMemo(
    () => filteredOrgs.filter((o) => o.reportReady && o.overall != null),
    [filteredOrgs]
  );

  /**
   * Active pillar/section keys for this workspace.
   * Custom-type workspaces: derived from the ``dimensions`` list on the first
   * report-ready org.  Classic 5X: the six hardcoded pillar keys (minus financial).
   */
  const activePillarKeys = useMemo(
    () => getActivePillarKeys(organisations),
    [organisations]
  );

  const structureMismatch = useMemo(
    () => hasAssessmentStructureMismatch(filteredOrgs),
    [filteredOrgs]
  );

  const cohortMedians = useMemo(() => {
    const out = {};
    activePillarKeys.forEach(({ key }) => {
      const vals = reportReadyOrgs
        .map((o) => getPillarScore(o, key))
        .filter((x) => x != null);
      out[key] = median(vals);
    });
    const overallVals = reportReadyOrgs.map((o) => o.overall).filter((x) => x != null);
    out.overall = median(overallVals);
    return out;
  }, [reportReadyOrgs, activePillarKeys]);

  const cohortImportanceMedians = useMemo(() => {
    const out = {};
    activePillarKeys.forEach(({ key }) => {
      const vals = reportReadyOrgs
        .map((o) => getPillarImportance(o, key))
        .filter((x) => x != null && !Number.isNaN(Number(x)))
        .map(Number);
      out[key] = median(vals);
    });
    return out;
  }, [reportReadyOrgs, activePillarKeys]);

  const cohortAgreementMedians = useMemo(() => {
    const out = {};
    activePillarKeys.forEach(({ key }) => {
      const vals = reportReadyOrgs
        .map((o) => getPillarAgreement(o, key))
        .filter((x) => x != null && !Number.isNaN(Number(x)))
        .map(Number);
      out[key] = median(vals);
    });
    return out;
  }, [reportReadyOrgs, activePillarKeys]);

  const focusOrg = useMemo(
    () => filteredOrgs.find((o) => o.id === focusOrgId) || null,
    [filteredOrgs, focusOrgId]
  );

  const compareOrgs = useMemo(
    () => filteredOrgs.filter((o) => compareIds.includes(o.id)),
    [filteredOrgs, compareIds]
  );

  const sortedFilteredOrgIds = useMemo(
    () => [...filteredOrgs].map((o) => o.id).sort(),
    [filteredOrgs]
  );

  const orgColorById = useMemo(
    () => buildOrgColorMap(compareIds, focusOrgId, sortedFilteredOrgIds),
    [compareIds, focusOrgId, sortedFilteredOrgIds]
  );

  /**
   * KPI/chart scope: focused organisation only (if set), else compare selection, else full filtered cohort.
   * Cohort median benchmark still uses all report-ready orgs passing filters.
   */
  const analysisOrgs = useMemo(() => {
    if (focusOrgId && focusOrg) return [focusOrg];
    if (compareIds.length > 0) return compareOrgs;
    return filteredOrgs;
  }, [focusOrgId, focusOrg, compareIds, compareOrgs, filteredOrgs]);

  const analysisReportReady = useMemo(
    () => analysisOrgs.filter((o) => o.reportReady && o.overall != null),
    [analysisOrgs]
  );

  /** All report-ready companies in loaded workspace — percentile baseline (ignores UI filters/focus). */
  const workspaceReportReady = useMemo(
    () => organisations.filter((o) => o.reportReady && o.overall != null),
    [organisations]
  );

  const workspacePercentileById = useMemo(
    () => buildWorkspacePercentileByOrgId(workspaceReportReady),
    [workspaceReportReady]
  );

  const kpis = useMemo(() => {
    const n = analysisOrgs.length;
    const sumInvited = analysisOrgs.reduce((s, o) => s + o.invited, 0);
    const sumCompleted = analysisOrgs.reduce((s, o) => s + o.completed, 0);
    const sumLiteCompleted = analysisOrgs.reduce((s, o) => s + (o.liteCompleted || 0), 0);
    const completionPcts = analysisOrgs.map((o) =>
      o.invited ? Math.round((o.completed / o.invited) * 100) : 0
    );
    const includeOrphanSurveys =
      usingLiveInsights &&
      unassignedSurveys &&
      unassignedSurveys.count > 0 &&
      !focusOrgId &&
      compareIds.length === 0 &&
      industry === FILTER_ALL_INDUSTRIES &&
      companySize === FILTER_ALL_COMPANY_SIZES &&
      !dateFrom &&
      !dateTo;
    const oInv = includeOrphanSurveys ? unassignedSurveys.count : 0;
    const oComp = includeOrphanSurveys ? unassignedSurveys.completed : 0;
    const oLite = includeOrphanSurveys ? unassignedSurveys.liteCompleted || 0 : 0;
    const oPend = includeOrphanSurveys ? unassignedSurveys.pending : 0;
    const oNs = includeOrphanSurveys ? unassignedSurveys.notStarted : 0;

    const participantsInvited = sumInvited + oInv;
    const participantsCompleted = sumCompleted + oComp;
    const participantsLiteCompleted = sumLiteCompleted + oLite;
    const avgCompletion =
      includeOrphanSurveys && participantsInvited > 0
        ? Math.round((participantsCompleted / participantsInvited) * 100)
        : avg(completionPcts);
    const overallScores = analysisReportReady.map((o) => o.overall);
    const avgOverall = avg(overallScores);
    const activeAssessments =
      analysisOrgs.reduce(
        (s, o) => s + (o.pending + o.notStarted + (o.liteCompleted || 0)),
        0
      ) +
      oPend +
      oNs +
      oLite;
    const reportReadyCount = analysisOrgs.filter((o) => o.reportReady).length;
    return {
      totalOrgs: n,
      avgCompletion,
      avgOverall,
      activeAssessments,
      reportReadyCount,
      participantsInvited,
      participantsCompleted,
      participantsLiteCompleted,
    };
  }, [
    analysisOrgs,
    analysisReportReady,
    usingLiveInsights,
    unassignedSurveys,
    focusOrgId,
    compareIds.length,
    industry,
    companySize,
    dateFrom,
    dateTo,
  ]);

  const toggleCompare = useCallback((id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARE_ORGS) return prev;
      return [...prev, id];
    });
  }, []);

  const openComparePicker = useCallback(() => {
    setCompareDraftIds([...compareIds]);
    setComparePickerOpen(true);
  }, [compareIds]);

  const toggleCompareDraft = useCallback((id) => {
    setCompareDraftIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARE_ORGS) return prev;
      return [...prev, id];
    });
  }, []);

  const applyComparePicker = useCallback(() => {
    setCompareIds(compareDraftIds);
    setComparePickerOpen(false);
  }, [compareDraftIds]);

  const cancelComparePicker = useCallback(() => {
    setComparePickerOpen(false);
  }, []);

  useEffect(() => {
    if (!comparePickerOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (comparePickerRef.current && !comparePickerRef.current.contains(e.target)) {
        setComparePickerOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setComparePickerOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [comparePickerOpen]);

  const compareTriggerSummary = useMemo(() => {
    if (compareIds.length === 0) return "Select companies…";
    const names = compareIds
      .map((id) => filteredOrgs.find((o) => o.id === id)?.name)
      .filter(Boolean);
    if (names.length <= 2) return names.join(", ");
    return `${names.length} companies selected`;
  }, [compareIds, filteredOrgs]);

  const clearFilters = () => {
    setIndustry(FILTER_ALL_INDUSTRIES);
    setCompanySize(FILTER_ALL_COMPANY_SIZES);
    setDateFrom("");
    setDateTo("");
    setFocusOrgId("");
    setCompareIds([]);
  };

  useEffect(() => {
    if (focusOrgId && !filteredOrgs.some((o) => o.id === focusOrgId)) {
      setFocusOrgId("");
    }
    setCompareIds((ids) => {
      const next = ids.filter((id) => filteredOrgs.some((o) => o.id === id));
      if (next.length === ids.length && next.every((id, i) => id === ids[i])) {
        return ids;
      }
      return next;
    });
  }, [filteredOrgs, focusOrgId]);

  const histogramOptions = useMemo(() => {
    const labels = ["0–20", "21–40", "41–60", "61–80", "81–100"];
    const bucket = (s) => {
      if (s <= 20) return 0;
      if (s <= 40) return 1;
      if (s <= 60) return 2;
      if (s <= 80) return 3;
      return 4;
    };
    const bucketDetails = [[], [], [], [], []];
    analysisReportReady.forEach((o) => {
      const b = bucket(o.overall);
      bucketDetails[b].push({ name: o.name, score: o.overall });
    });
    const counts = bucketDetails.map((arr) => arr.length);
    return {
      chart: { ...CHART_FLUID, type: "column", height: 260 },
      credits: { enabled: false },
      title: { text: "Report-ready companies by score band" },
      xAxis: { categories: labels, crosshair: true },
      yAxis: { min: 0, title: { text: "Companies" }, allowDecimals: false },
      plotOptions: { column: { borderRadius: 2, color: "#004f73" } },
      tooltip: scrollableTooltipOptions(function () {
        // Highcharts may pass category index as this.x (0–4) instead of the label string — map to score bands.
        const idx =
          typeof this.point?.index === "number"
            ? this.point.index
            : typeof this.x === "number"
              ? this.x
              : labels.indexOf(this.x);
        const safeIdx = idx >= 0 && idx < bucketDetails.length ? idx : 0;
        const orgsInBucket = bucketDetails[safeIdx];
        const n = orgsInBucket.length;
        const header = `<b>Total companies:</b> ${n}`;
        if (n === 0) {
          return `${header}<br/><span style="color:#64748b">No companies in this band</span>`;
        }
        const lines = orgsInBucket
          .map((row) => `${row.name}: <b>${row.score}</b>`)
          .join("<br/>");
        return `${header}<br/>${lines}`;
      }),
      series: [{ name: "Count", data: counts, showInLegend: false }],
    };
  }, [analysisReportReady]);

  const pillarCohortOptions = useMemo(() => {
    const categories = activePillarKeys.map((p) => p.label);
    const data = activePillarKeys.map(({ key }) =>
      cohortMedians[key] != null ? Math.round(cohortMedians[key]) : 0
    );
    const series = [
      {
        name: "Group Benchmark",
        data,
        color: COHORT_MEDIAN_COLOR,
        borderColor: "#334155",
        borderWidth: 1,
      },
    ];
    const added = new Set();
    const pushOrgSeries = (org) => {
      if (!org.reportReady) return;
      if (added.has(org.id)) return;
      added.add(org.id);
      const color = orgColorById.get(org.id) ?? NEUTRAL_ORG_COLOR;
      series.push({
        name: org.name,
        data: activePillarKeys.map(({ key }) => {
          const v = getPillarScore(org, key);
          return v != null ? Math.round(v) : 0;
        }),
        color,
      });
    };
    if (focusOrg) pushOrgSeries(focusOrg);
    compareOrgs.forEach((org) => pushOrgSeries(org));
    return {
      chart: { ...CHART_FLUID, type: "column", height: 280 },
      credits: { enabled: false },
      title: { text: "Pillar profile" },
      xAxis: {
        categories,
        labels: { style: { fontSize: "9px" }, autoRotation: [-40], autoRotationLimit: 80 },
      },
      yAxis: { min: 0, max: 100, title: { text: "Score" } },
      plotOptions: { column: { grouping: true, borderWidth: 0 } },
      tooltip: { shared: true },
      series,
    };
  }, [cohortMedians, focusOrg, compareOrgs, orgColorById, activePillarKeys]);

  const pillarImportanceCohortOptions = useMemo(() => {
    const categories = activePillarKeys.map((p) => p.label);
    const roundImp = (v) =>
      v != null && !Number.isNaN(Number(v)) ? Math.round(Number(v) * 10) / 10 : null;
    const benchmarkData = activePillarKeys.map(({ key }) => {
      const m = cohortImportanceMedians[key];
      const r = roundImp(m);
      return r != null ? r : 0;
    });
    const orgSeries = [];
    const added = new Set();
    const pushOrgSeries = (org) => {
      if (!orgHasImportanceScores(org)) return;
      if (added.has(org.id)) return;
      added.add(org.id);
      const color = orgColorById.get(org.id) ?? NEUTRAL_ORG_COLOR;
      orgSeries.push({
        type: "column",
        name: org.name,
        data: activePillarKeys.map(({ key }) => {
          const r = roundImp(getPillarImportance(org, key));
          return r != null ? r : 0;
        }),
        color,
      });
    };
    if (focusOrg) pushOrgSeries(focusOrg);
    compareOrgs.forEach((org) => pushOrgSeries(org));
    const series = [
      ...orgSeries,
      {
        type: "line",
        name: "Group Benchmark",
        data: benchmarkData,
        color: COHORT_MEDIAN_COLOR,
        lineWidth: 2,
        marker: {
          enabled: true,
          radius: 4,
          lineWidth: 1,
          lineColor: "#334155",
          fillColor: COHORT_MEDIAN_COLOR,
        },
        zIndex: 5,
      },
    ];
    return {
      chart: { ...CHART_FLUID, type: "column", height: 280 },
      credits: { enabled: false },
      title: { text: "Importance Scores" },
      subtitle: {
        text: "Scale: 0–9",
        style: { color: "#64748b", fontSize: "11px" },
      },
      xAxis: {
        categories,
        labels: { style: { fontSize: "9px" }, autoRotation: [-40], autoRotationLimit: 80 },
      },
      yAxis: { min: 0, max: 9, title: { text: "Average importance" } },
      plotOptions: {
        column: { grouping: true, borderWidth: 0 },
        line: { lineWidth: 2 },
      },
      tooltip: {
        shared: true,
        valueDecimals: 1,
      },
      series,
    };
  }, [cohortImportanceMedians, focusOrg, compareOrgs, orgColorById, activePillarKeys]);

  const pillarAgreementCohortOptions = useMemo(() => {
    const categories = activePillarKeys.map((p) => p.label);
    const roundAgr = (v) =>
      v != null && !Number.isNaN(Number(v)) ? Math.round(Number(v) * 10) / 10 : null;
    const benchmarkData = activePillarKeys.map(({ key }) => {
      const m = cohortAgreementMedians[key];
      const r = roundAgr(m);
      return r != null ? r : 0;
    });
    const orgSeries = [];
    const added = new Set();
    const pushOrgSeries = (org) => {
      if (!orgHasAgreementScores(org)) return;
      if (added.has(org.id)) return;
      added.add(org.id);
      const color = orgColorById.get(org.id) ?? NEUTRAL_ORG_COLOR;
      orgSeries.push({
        type: "column",
        name: org.name,
        data: activePillarKeys.map(({ key }) => {
          const r = roundAgr(getPillarAgreement(org, key));
          return r != null ? r : 0;
        }),
        color,
      });
    };
    if (focusOrg) pushOrgSeries(focusOrg);
    compareOrgs.forEach((org) => pushOrgSeries(org));
    const series = [
      ...orgSeries,
      {
        type: "line",
        name: "Group Benchmark",
        data: benchmarkData,
        color: COHORT_MEDIAN_COLOR,
        lineWidth: 2,
        marker: {
          enabled: true,
          radius: 4,
          lineWidth: 1,
          lineColor: "#334155",
          fillColor: COHORT_MEDIAN_COLOR,
        },
        zIndex: 5,
      },
    ];
    return {
      chart: { ...CHART_FLUID, type: "column", height: 280 },
      credits: { enabled: false },
      title: { text: "Agreement Scores" },
      subtitle: {
        text: "Scale: 1–5",
        style: { color: "#64748b", fontSize: "11px" },
      },
      xAxis: {
        categories,
        labels: { style: { fontSize: "9px" }, autoRotation: [-40], autoRotationLimit: 80 },
      },
      yAxis: { min: 0, max: 5, title: { text: "Average agreement" } },
      plotOptions: {
        column: { grouping: true, borderWidth: 0 },
        line: { lineWidth: 2 },
      },
      tooltip: {
        shared: true,
        valueDecimals: 1,
      },
      series,
    };
  }, [cohortAgreementMedians, focusOrg, compareOrgs, orgColorById, activePillarKeys]);

  /** Same scope rules as KPIs, but explicit so participation chart always matches filter/focus/compare */
  const participationOrgsSlice = useMemo(() => {
    let source;
    if (focusOrgId && focusOrg) source = [focusOrg];
    else if (compareIds.length > 0) source = compareOrgs;
    else source = filteredOrgs;
    return [...source]
      .sort((a, b) => b.invited - a.invited)
      .slice(0, MAX_ORGS_PER_PARTICIPATION_CHART);
  }, [focusOrgId, focusOrg, compareIds, compareOrgs, filteredOrgs]);

  /**
   * Exactly one org in scope via focus (dropdown or table row) OR one compare checkbox → 3 bars.
   * No selection / full cohort, or 2+ compare orgs → stacked by organisation.
   */
  const participationThreeBarActive = useMemo(() => {
    const orgs = participationOrgsSlice;
    if (orgs.length !== 1) return false;
    const row = orgs[0];
    if (focusOrgId && focusOrg && row.id === focusOrg.id) return true;
    if (!focusOrgId && compareIds.length === 1 && compareOrgs[0]?.id === row.id) return true;
    return false;
  }, [participationOrgsSlice, focusOrgId, focusOrg, compareIds, compareOrgs]);

  const participationByOrgOptions = useMemo(() => {
    const orgs = participationOrgsSlice;
    if (orgs.length === 0) {
      return {
        chart: { ...CHART_FLUID, type: "column", height: 260 },
        credits: { enabled: false },
        title: { text: "Assessment participation" },
        xAxis: { categories: [] },
        yAxis: { min: 0, title: { text: "People" } },
        series: [],
      };
    }

    if (participationThreeBarActive) {
      const o = orgs[0];
      return {
        chart: { ...CHART_FLUID, type: "column", height: 280 },
        credits: { enabled: false },
        title: { text: "Assessment participation" },
        subtitle: { text: o.name, style: { color: "#334155", fontSize: "11px" } },
        legend: { enabled: false },
        xAxis: {
          categories: ["Full completed", "Lite completed", "Pending", "Not started"],
          crosshair: true,
        },
        yAxis: { min: 0, title: { text: "People" }, allowDecimals: false },
        plotOptions: {
          column: {
            borderRadius: 3,
            borderWidth: 0,
            colorByPoint: true,
            stacking: false,
          },
        },
        tooltip: scrollableTooltipOptions(function () {
          return `<b>${o.name}</b><br/>${this.x}: <b>${this.y}</b> people`;
        }),
        series: [
          {
            name: shortOrgLabel(o.name, 24),
            data: [
              { y: o.completed, color: PARTICIPANT_COMPLETED },
              { y: o.liteCompleted || 0, color: PARTICIPANT_LITE_COMPLETED },
              { y: o.pending, color: PARTICIPANT_PENDING },
              { y: o.notStarted, color: PARTICIPANT_NOT_STARTED },
            ],
          },
        ],
      };
    }
    const categories = orgs.map((o) => shortOrgLabel(o.name, 18));
    const needsRotation = categories.some((c) => c.length > 10);
    const chartHeight = Math.min(Math.max(300, 240 + orgs.length * 14), 480);
    return {
      chart: {
        ...CHART_FLUID,
        type: "column",
        height: chartHeight,
        spacingTop: 16,
        spacingBottom: 10,
      },
      credits: { enabled: false },
      title: { text: "Assessment participation", margin: 16 },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: { fontSize: "10px" },
      },
      xAxis: {
        categories,
        labels: fullCategoryAxisLabels({
          autoRotation: needsRotation ? [-45] : 0,
          y: needsRotation ? 14 : 6,
        }),
      },
      yAxis: { min: 0, title: { text: "People" }, allowDecimals: false, stackLabels: { enabled: false } },
      plotOptions: {
        column: { stacking: "normal", borderWidth: 0, grouping: false },
      },
      tooltip: participationColumnTooltip(orgs),
      series: [
        {
          name: "Full completed",
          data: orgs.map((o) => o.completed),
          color: PARTICIPANT_COMPLETED,
          stack: "people",
        },
        {
          name: "Lite completed",
          data: orgs.map((o) => o.liteCompleted || 0),
          color: PARTICIPANT_LITE_COMPLETED,
          stack: "people",
        },
        {
          name: "Pending",
          data: orgs.map((o) => o.pending),
          color: PARTICIPANT_PENDING,
          stack: "people",
        },
        {
          name: "Not started",
          data: orgs.map((o) => o.notStarted),
          color: PARTICIPANT_NOT_STARTED,
          stack: "people",
        },
      ],
    };
  }, [participationOrgsSlice, participationThreeBarActive]);

  /** Highcharts can keep stacking/categories when switching focus 3-bar ↔ cohort stacked — remount fixes it */
  const participationChartRemountKey = useMemo(() => {
    const orgs = participationOrgsSlice;
    const orgIds = orgs.map((o) => o.id).join("-");
    const filterSig = `${industry}|${companySize}|${dateFrom}|${dateTo}`;
    const compareSig = compareIds.join(",");
    return participationThreeBarActive
      ? `p3-${orgs[0]?.id ?? "x"}-${filterSig}-${compareSig}-f${focusOrgId || "0"}`
      : `pst-${orgIds}-${filterSig}-${compareSig}-${filteredOrgs.length}`;
  }, [
    participationOrgsSlice,
    participationThreeBarActive,
    focusOrgId,
    industry,
    companySize,
    dateFrom,
    dateTo,
    compareIds,
    filteredOrgs.length,
  ]);

  /** Remount weekly chart when scope/filters change — avoids stale x-axis (index vs date labels). */
  const weeklyChartRemountKey = useMemo(() => {
    const catSig = weekCategories.join("|");
    const mode = focusOrgId
      ? `focus-${focusOrgId}`
      : compareIds.length >= 2
        ? `cmp-${compareIds.join(",")}`
        : "agg";
    const filterSig = `${industry}|${companySize}|${dateFrom}|${dateTo}`;
    return `wk-${mode}-${catSig}-${filterSig}-${analysisOrgs.length}`;
  }, [
    weekCategories,
    focusOrgId,
    compareIds,
    industry,
    companySize,
    dateFrom,
    dateTo,
    analysisOrgs.length,
  ]);

  const trendOptions = useMemo(() => {
    const weeks = weekCategories;
    const yTitle = "Completions";

    if (analysisOrgs.length === 0) {
      return {
        chart: { ...CHART_FLUID, height: 280 },
        credits: { enabled: false },
        title: { text: "Weekly completions" },
        xAxis: weeklyXAxis(weeks),
        yAxis: { min: 0, title: { text: yTitle } },
        series: [],
      };
    }

    /** No focus: 2+ organisations in compare → one line per org; otherwise single aggregate line */
    if (!focusOrgId) {
      const multiCompare =
        compareIds.length >= 2 && compareOrgs.length >= 2;
      if (multiCompare) {
        const seriesOrgs = compareIds
          .map((id) => compareOrgs.find((o) => o.id === id))
          .filter(Boolean)
          .slice(0, MAX_WEEKLY_COMPARE_LINES);
        const n = seriesOrgs.length;
        return {
          chart: { ...CHART_FLUID, height: Math.min(400, 220 + n * 14) },
          credits: { enabled: false },
          title: { text: "Weekly completions" },
          legend: {
            enabled: true,
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            itemStyle: { fontSize: "10px" },
          },
          xAxis: weeklyXAxis(weeks),
          yAxis: { min: 0, title: { text: yTitle }, allowDecimals: false },
          tooltip: {
            shared: true,
            ...INSIGHTS_SCROLLABLE_TOOLTIP,
            formatter: lineChartSharedTooltipBySeriesIndex(seriesOrgs, weeks),
          },
          plotOptions: {
            line: { marker: { enabled: n <= 5, radius: 3 } },
          },
          series: seriesOrgs.map((org) => ({
            type: "line",
            name: shortOrgLabel(org.name, 22),
            data: weeklyCompletionsSeriesForOrg(org, weeklyTrend),
            color: orgColorById.get(org.id) ?? NEUTRAL_ORG_COLOR,
            lineWidth: 2,
          })),
        };
      }

      const agg = weeklyTrendAggregateCompletions(analysisOrgs, weeklyTrend);
      const liteAgg = weeklyTrendAggregateLiteCompletions(analysisOrgs, weeklyTrend);
      const hasLite = liteAgg.some((v) => v > 0);
      return {
        chart: { ...CHART_FLUID, height: 280 },
        credits: { enabled: false },
        title: { text: "Weekly completions" },
        legend: {
          enabled: hasLite,
          layout: "horizontal",
          align: "center",
          verticalAlign: "bottom",
          itemStyle: { fontSize: "10px" },
        },
        xAxis: weeklyXAxis(weeks),
        yAxis: { min: 0, title: { text: yTitle }, allowDecimals: false },
        tooltip: hasLite
          ? { ...INSIGHTS_SCROLLABLE_TOOLTIP, shared: true }
          : { ...INSIGHTS_SCROLLABLE_TOOLTIP, formatter: weeklySingleLineTooltip(weeks) },
        plotOptions: {
          line: { marker: { enabled: true, radius: 3 } },
        },
        series: [
          {
            type: "line",
            name: "Full completed",
            data: agg,
            color: PARTICIPANT_COMPLETED,
            lineWidth: 2,
          },
          ...(hasLite
            ? [
                {
                  type: "line",
                  name: "Lite completed",
                  data: liteAgg,
                  color: PARTICIPANT_LITE_COMPLETED,
                  lineWidth: 2,
                  dashStyle: "ShortDash",
                },
              ]
            : []),
        ],
      };
    }

    /* Focus set (dropdown or table): one org line */
    if (!focusOrg) {
      return {
        chart: { ...CHART_FLUID, height: 280 },
        credits: { enabled: false },
        title: { text: "Weekly completions" },
        xAxis: weeklyXAxis(weeks),
        yAxis: { min: 0, title: { text: yTitle } },
        series: [],
      };
    }
    const org = focusOrg;
    const focusLiteData = weeklyLiteCompletionsSeriesForOrg(org, weeklyTrend);
    const focusHasLite = focusLiteData.some((v) => v > 0);
    return {
      chart: { ...CHART_FLUID, height: 280 },
      credits: { enabled: false },
      title: { text: "Weekly completions" },
      legend: {
        enabled: focusHasLite,
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
        itemStyle: { fontSize: "10px" },
      },
      xAxis: weeklyXAxis(weeks),
      yAxis: { min: 0, title: { text: yTitle }, allowDecimals: false },
      tooltip: focusHasLite
        ? { ...INSIGHTS_SCROLLABLE_TOOLTIP, shared: true }
        : scrollableTooltipOptions(function () {
            const week = weekTooltipLabel(weeks, this.point, this.x);
            return `<b>${org.name}</b><br/>${week}: <b>${this.y}</b> completions`;
          }),
      plotOptions: {
        line: { marker: { enabled: true, radius: 3 } },
      },
      series: (() => {
        const fullData = weeklyCompletionsSeriesForOrg(org, weeklyTrend);
        const liteData = focusLiteData;
        const hasLite = focusHasLite;
        const series = [
          {
            type: "line",
            name: "Full completed",
            data: fullData,
            color: PARTICIPANT_COMPLETED,
            lineWidth: 2,
          },
        ];
        if (hasLite) {
          series.push({
            type: "line",
            name: "Lite completed",
            data: liteData,
            color: PARTICIPANT_LITE_COMPLETED,
            lineWidth: 2,
            dashStyle: "ShortDash",
          });
        }
        return series;
      })(),
    };
  }, [
    analysisOrgs,
    compareIds,
    compareOrgs,
    focusOrgId,
    focusOrg,
    orgColorById,
    weekCategories,
    weeklyTrend,
  ]);

  const percentileOptions = useMemo(() => {
    const displaySortedDesc = [...analysisReportReady].sort(
      (a, b) => b.overall - a.overall || String(a.name).localeCompare(String(b.name))
    );
    const cohortDefault = !focusOrgId && compareIds.length === 0;
    const displayList =
      cohortDefault && displaySortedDesc.length > MAX_OVERALL_RANK_CHART_ORGS
        ? displaySortedDesc.slice(0, MAX_OVERALL_RANK_CHART_ORGS)
        : displaySortedDesc;

    const n = displayList.length;

    if (n === 0) {
      return {
        chart: { ...CHART_FLUID, type: "bar", height: 200 },
        credits: { enabled: false },
        title: { text: "Performance ranking" },
        xAxis: { categories: ["—"], title: { text: null } },
        yAxis: { min: 0, max: 100, title: { text: "Overall score" } },
        series: [{ name: "Overall", data: [{ y: 0, color: "#e2e8f0" }], showInLegend: false }],
      };
    }
    const categories = displayList.map((o) => o.name);
    const pointData = displayList.map((o) => ({
      y: o.overall,
      orgName: o.name,
      percentile: workspacePercentileById.get(o.id) ?? 0,
      color: orgColorById.get(o.id) ?? NEUTRAL_ORG_COLOR,
    }));
    const maxNameLen = categories.reduce((m, name) => Math.max(m, name.length), 0);
    const rowHeight = Math.max(24, Math.min(36, 20 + Math.ceil(maxNameLen / 20) * 4));
    const chartHeight = Math.min(Math.max(200, n * rowHeight), 560);
    return {
      chart: {
        ...CHART_FLUID,
        type: "bar",
        height: chartHeight,
        spacingLeft: 6,
      },
      credits: { enabled: false },
      title: { text: "Performance ranking" },
      xAxis: {
        type: "category",
        categories,
        title: { text: null },
        labels: fullCategoryAxisLabels({
          align: "right",
          x: -2,
          width: categoryLabelWidthPx(categories),
        }),
      },
      yAxis: { min: 0, max: 100, title: { text: "Overall score" } },
      plotOptions: { bar: { dataLabels: { enabled: false }, borderWidth: 0 } },
      tooltip: scrollableTooltipOptions(function () {
        const fullName = this.point?.orgName ?? this.key ?? this.x;
        return `<b>${fullName}</b><br/><b>${this.y}</b> overall<br/><b>${this.point.percentile}</b>th percentile amongst all companies`;
      }),
      series: [
        {
          name: "Overall",
          data: pointData,
          showInLegend: false,
        },
      ],
    };
  }, [analysisReportReady, workspacePercentileById, orgColorById, compareIds, focusOrgId]);

  const tableColumns = useMemo(() => {
    const companyHeading = titleCaseHeading("Company");
    const companyColWidth = tableColWidthForContent(
      companyHeading,
      masterTableRows.map((r) => r.name),
      160
    );
    const industryHeading = titleCaseHeading("Industry");
    const industryColWidth = tableColWidthForContent(
      industryHeading,
      masterTableRows.map((r) => r.industry),
      120
    );
    return [
      {
        id: "compare",
        name: titleCaseHeading("Compare"),
        width: tableColWidth(titleCaseHeading("Compare"), 76),
        minWidth: tableColWidth(titleCaseHeading("Compare"), 76),
        wrap: false,
        center: true,
        cell: (row) => (
          <input
            type="checkbox"
            checked={compareIds.includes(row.id)}
            disabled={!compareIds.includes(row.id) && compareIds.length >= MAX_COMPARE_ORGS}
            onChange={() => toggleCompare(row.id)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Add ${row.name} to compare`}
          />
        ),
      },
      {
        id: "name",
        name: companyHeading,
        selector: (row) => row.name,
        sortable: true,
        width: companyColWidth,
        minWidth: companyColWidth,
        wrap: true,
        style: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflow: "visible",
          textOverflow: "clip",
        },
        cell: (row) => {
          const flaggedAnomaly = anomalies.find(
            (a) => a.title && row.name && a.title.toLowerCase().includes(row.name.toLowerCase())
          );
          const zeroCompletion = row.invited > 0 && row.completed === 0 && !row.liteCompleted;
          return (
            <div className="flex flex-col gap-0.5 py-1 min-w-0">
              <span className="whitespace-normal break-words leading-snug">{row.name}</span>
              {flaggedAnomaly && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 w-fit">
                  <svg className="w-2.5 h-2.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  AI signal
                </span>
              )}
              {!flaggedAnomaly && zeroCompletion && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 w-fit">
                  No completions
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "industry",
        name: industryHeading,
        selector: (row) => row.industry,
        sortable: true,
        width: industryColWidth,
        minWidth: industryColWidth,
        wrap: true,
        style: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflow: "visible",
          textOverflow: "clip",
        },
        cell: (row) => (
          <span className="whitespace-normal break-words leading-snug">{row.industry}</span>
        ),
      },
      {
        id: "size",
        name: "Size (Employee Count)",
        selector: (row) => formatCompanySizeForTable(row.companySize),
        sortable: true,
        width: tableColWidth("Size (Employee Count)", 168),
        minWidth: tableColWidth("Size (Employee Count)", 168),
        wrap: false,
      },
      ...(() => {
        const invitedHeading = titleCaseHeading("Invited");
        const invitedWidth = tableColWidth(invitedHeading, 88);
        const fullCompletedHeading = titleCaseHeading("Full completed");
        const fullCompletedWidth = tableColWidth(fullCompletedHeading, 132);
        const liteCompletedHeading = titleCaseHeading("Lite completed");
        const liteCompletedWidth = tableColWidth(liteCompletedHeading, 132);
        const completionHeading = titleCaseHeading("Completion %");
        const completionWidth = tableColWidth(completionHeading, 124);
        const reportReadyHeading = titleCaseHeading("Report ready");
        const reportReadyWidth = tableColWidth(reportReadyHeading, 124);
        const overallHeading = titleCaseHeading("Overall");
        const overallWidth = tableColWidth(overallHeading, 96);
        return [
      {
        id: "invited",
        name: invitedHeading,
        selector: (row) => row.invited,
        sortable: true,
        width: invitedWidth,
        minWidth: invitedWidth,
        wrap: false,
      },
      {
        id: "completed",
        name: fullCompletedHeading,
        selector: (row) => row.completed,
        sortable: true,
        width: fullCompletedWidth,
        minWidth: fullCompletedWidth,
        wrap: false,
      },
      {
        id: "liteCompleted",
        name: liteCompletedHeading,
        selector: (row) => row.liteCompleted || 0,
        sortable: true,
        width: liteCompletedWidth,
        minWidth: liteCompletedWidth,
        wrap: false,
      },
      {
        id: "completionPct",
        name: completionHeading,
        selector: (row) => (row.invited ? Math.round((row.completed / row.invited) * 100) : 0),
        sortable: true,
        width: completionWidth,
        minWidth: completionWidth,
        wrap: false,
      },
      {
        id: "ready",
        name: reportReadyHeading,
        selector: (row) => (row.reportReady ? "Yes" : "No"),
        width: reportReadyWidth,
        minWidth: reportReadyWidth,
        wrap: false,
      },
      {
        id: "overall",
        name: overallHeading,
        selector: (row) => row.overall ?? "—",
        sortable: true,
        width: overallWidth,
        minWidth: overallWidth,
        wrap: false,
      },
        ];
      })(),
      ...activePillarKeys.map(({ key, label }) => {
        const heading = titleCaseHeading(label);
        const colWidth = tableColWidth(heading, 132);
        return {
          id: `pillar_${key}`,
          name: heading,
          selector: (row) => {
            const v = getPillarScore(row, key);
            return v != null ? Math.round(v) : "—";
          },
          sortable: true,
          width: colWidth,
          minWidth: colWidth,
          wrap: false,
        };
      }),
      {
        id: "lastActivity",
        name: titleCaseHeading("Last activity"),
        selector: (row) => row.lastActivity,
        sortable: true,
        width: tableColWidth(titleCaseHeading("Last activity"), 124),
        minWidth: tableColWidth(titleCaseHeading("Last activity"), 124),
        wrap: false,
      },
    ];
  }, [compareIds, toggleCompare, masterTableRows, activePillarKeys]);

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row) => row.id === focusOrgId,
        style: {
          backgroundColor: dark ? "rgba(34, 211, 238, 0.1)" : "rgba(0, 79, 115, 0.08)",
          fontWeight: 600,
          color: dark ? "#f1f5f9" : "#0f172a",
        },
      },
    ],
    [focusOrgId, dark]
  );

  const focusVsMedian = useMemo(() => {
    if (!focusOrg || !focusOrg.reportReady || cohortMedians.overall == null) return null;
    const delta = Math.round(focusOrg.overall - cohortMedians.overall);
    return { delta, cohort: cohortMedians.overall };
  }, [focusOrg, cohortMedians]);

  /** Short label for who the dashboard KPIs/charts are scoped to (shown under the title). */
  const scopeLine = useMemo(() => {
    if (focusOrgId && focusOrg) {
      return { label: "Focused company", text: focusOrg.name };
    }
    if (compareIds.length > 0) {
      const names = compareIds
        .map((id) => filteredOrgs.find((o) => o.id === id)?.name)
        .filter(Boolean);
      const joined = names.join(", ");
      return {
        label: compareIds.length === 1 ? "Company in compare" : "Companies in compare",
        text: joined || `${compareIds.length} selected`,
      };
    }
    return {
      label: "Scope",
      text:
        filteredOrgs.length === 1
          ? "1 company matches your filters"
          : `${filteredOrgs.length} companies match your filters`,
    };
  }, [focusOrgId, focusOrg, compareIds, filteredOrgs]);

  const companyComparison = useMemo(() => {
    let mode = "cohort";
    let sourceOrgs = [];

    if (focusOrgId && focusOrg) {
      return {
        mode: "focus",
        count: 1,
        reportReadyCount: focusOrg.reportReady && focusOrg.overall != null ? 1 : 0,
        spread: null,
        ranked: [buildNarrativeComparisonEntry(focusOrg, workspacePercentileById, cohortMedians, activePillarKeys)],
        highestCompletion: null,
        lowestCompletion: null,
      };
    }

    if (compareIds.length > 0) {
      mode = "compare";
      sourceOrgs = [...compareOrgs].sort(
        (a, b) => (b.overall ?? -1) - (a.overall ?? -1) || String(a.name).localeCompare(String(b.name))
      );
    } else if (analysisOrgs.length >= 2) {
      mode = "cohort";
      const readySorted = [...analysisReportReady].sort(
        (a, b) => (b.overall ?? 0) - (a.overall ?? 0) || String(a.name).localeCompare(String(b.name))
      );
      sourceOrgs =
        readySorted.length >= 2
          ? readySorted
          : [...analysisOrgs].sort(
              (a, b) =>
                (b.overall ?? -1) - (a.overall ?? -1) ||
                String(a.name).localeCompare(String(b.name))
            );
    } else {
      return {
        mode,
        count: 0,
        reportReadyCount: 0,
        spread: null,
        ranked: [],
        highestCompletion: null,
        lowestCompletion: null,
      };
    }

    const ranked = sourceOrgs
      .slice(0, 12)
      .map((o) => buildNarrativeComparisonEntry(o, workspacePercentileById, cohortMedians, activePillarKeys));
    const withOverall = ranked.filter((r) => r.overall != null);
    const spread =
      withOverall.length >= 2
        ? {
            overallMin: Math.min(...withOverall.map((r) => r.overall)),
            overallMax: Math.max(...withOverall.map((r) => r.overall)),
            overallGap:
              Math.max(...withOverall.map((r) => r.overall)) -
              Math.min(...withOverall.map((r) => r.overall)),
          }
        : null;
    const completionRanked = [...ranked]
      .filter((r) => r.completionPct != null)
      .sort((a, b) => b.completionPct - a.completionPct);

    return {
      mode,
      count: ranked.length,
      reportReadyCount: ranked.filter((r) => r.reportReady && r.overall != null).length,
      spread,
      ranked,
      highestCompletion: completionRanked[0] || null,
      lowestCompletion:
        completionRanked.length >= 2
          ? completionRanked[completionRanked.length - 1]
          : null,
    };
  }, [
    focusOrgId,
    focusOrg,
    compareIds,
    compareOrgs,
    analysisOrgs,
    analysisReportReady,
    workspacePercentileById,
    cohortMedians,
  ]);

  const narrativeContext = useMemo(() => {
    const sortedReady = [...analysisReportReady].sort(
      (a, b) => (b.overall ?? 0) - (a.overall ?? 0)
    );
    const companySnapshot = (o) => ({
      name: o.name,
      industry: o.industry,
      companySize: o.companySize,
      yearOfFoundation: o.yearOfFoundation,
      revenueLastYear: o.revenueLastYear,
      revenueLast12Months: o.revenueLast12Months,
      ebita: o.ebita,
      invited: o.invited,
      completed: o.completed,
      liteCompleted: o.liteCompleted || 0,
      pending: o.pending,
      notStarted: o.notStarted,
      overall: o.overall,
      reportReady: o.reportReady,
      strategic: o.strategic,
      scalable: o.scalable,
      relentless: o.relentless,
      disciplined: o.disciplined,
      energized: o.energized,
    });

    const includeOrphanSurveys =
      usingLiveInsights &&
      unassignedSurveys &&
      unassignedSurveys.count > 0 &&
      !focusOrgId &&
      compareIds.length === 0 &&
      industry === FILTER_ALL_INDUSTRIES &&
      companySize === FILTER_ALL_COMPANY_SIZES &&
      !dateFrom &&
      !dateTo;

    const usesCustomDimensions = (organisations || []).some(
      (o) => Array.isArray(o.dimensions) && o.dimensions.length > 0
    );

    return {
      scope: scopeLine,
      isFiveX: !usesCustomDimensions,
      dimensionLabels: activePillarKeys.map((p) => p.label),
      filters: {
        industry,
        companySize,
        dateFrom,
        dateTo,
      },
      kpis,
      focusOrg: focusOrg ? companySnapshot(focusOrg) : null,
      compareCompanies: compareOrgs.map(companySnapshot),
      companies: analysisOrgs.slice(0, 20).map(companySnapshot),
      cohortMedians: {
        overall: cohortMedians.overall,
        strategic: cohortMedians.strategic,
        scalable: cohortMedians.scalable,
        relentless: cohortMedians.relentless,
        disciplined: cohortMedians.disciplined,
        energized: cohortMedians.energized,
      },
      topPerformers: sortedReady.slice(0, 3).map(companySnapshot),
      bottomPerformers: sortedReady.slice(-3).reverse().map(companySnapshot),
      companyComparison,
      unassignedSurveys: includeOrphanSurveys ? unassignedSurveys : null,
      workspaceReportReadyCount: workspaceReportReady.length,
    };
  }, [
    scopeLine,
    industry,
    companySize,
    dateFrom,
    dateTo,
    kpis,
    focusOrg,
    compareOrgs,
    analysisOrgs,
    analysisReportReady,
    cohortMedians,
    companyComparison,
    usingLiveInsights,
    unassignedSurveys,
    focusOrgId,
    compareIds.length,
    workspaceReportReady.length,
    organisations,
    activePillarKeys,
  ]);

  const chartExplainContextById = useMemo(
    () =>
      buildInsightsChartExplainContexts({
        analysisReportReady,
        workspacePercentileById,
        focusOrgId,
        compareIds,
        filteredOrgs,
        participationOrgsSlice,
        participationThreeBarActive,
        weekCategories,
        weeklyTrend,
        analysisOrgs,
        cohortMedians,
        focusOrg,
        compareOrgs,
        industry,
        companySize,
        dateFrom,
        dateTo,
        scopeLine,
        activePillarKeys,
      }),
    [
      analysisReportReady,
      workspacePercentileById,
      focusOrgId,
      compareIds,
      filteredOrgs,
      participationOrgsSlice,
      participationThreeBarActive,
      weekCategories,
      weeklyTrend,
      analysisOrgs,
      cohortMedians,
      focusOrg,
      compareOrgs,
      industry,
      companySize,
      dateFrom,
      dateTo,
      scopeLine,
      activePillarKeys,
    ]
  );

  useEffect(() => {
    setChartExplainById({});
  }, [chartExplainContextById]);

  const handleExplainChart = useCallback(
    (chartId) => {
      const context = chartExplainContextById[chartId];
      if (!context) return;
      setChartExplainLoadingById((prev) => ({ ...prev, [chartId]: true }));
      const explanation = formatChartExplanation(context);
      setChartExplainById((prev) => ({
        ...prev,
        [chartId]: { explanation, source: "structured" },
      }));
      setChartExplainLoadingById((prev) => ({ ...prev, [chartId]: false }));
    },
    [chartExplainContextById]
  );

  const loadAiSummary = useCallback(async () => {
    if (insightsLoading || scoresLoading || !usingLiveInsights || aiSummaryBusyRef.current) return;
    aiSummaryBusyRef.current = true;
    setNarrativeLoading(true);
    setAnomaliesDismissed(false);
    try {
      const anomalyContext = {
        companies: analysisOrgs.slice(0, 200).map((o) => ({
          name: o.name,
          invited: o.invited,
          completed: o.completed,
          overall: o.overall,
          ...Object.fromEntries(
            ["strategic", "scalable", "relentless", "disciplined", "energized", "financial"]
              .filter((k) => o[k] != null)
              .map((k) => [k, o[k]])
          ),
        })),
      };
      const [narrativeRes, anomalyRes] = await Promise.allSettled([
        fetchInsightsNarrative(narrativeContext),
        fetchInsightsAnomalies(anomalyContext),
      ]);
      if (narrativeRes.status === "fulfilled") {
        setNarrative(narrativeRes.value?.data?.narrative || "");
        setNarrativeSource(narrativeRes.value?.data?.source || "");
      } else {
        setNarrative("");
        setNarrativeSource("");
        toast.error(narrativeRes.reason?.message || "Could not generate AI summary");
      }
      if (anomalyRes.status === "fulfilled") {
        setAnomalies(anomalyRes.value?.data?.highlights || []);
      }
    } finally {
      aiSummaryBusyRef.current = false;
      setNarrativeLoading(false);
    }
  }, [
    analysisOrgs,
    insightsLoading,
    scoresLoading,
    usingLiveInsights,
    narrativeContext,
  ]);

  useEffect(() => {
    setNarrative("");
    setNarrativeSource("");
    setAnomalies([]);
    setAnomaliesDismissed(false);
  }, [narrativeContext]);

  useEffect(() => {
    if (insightsLoading || scoresLoading || !usingLiveInsights) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!cancelled) loadAiSummary();
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [narrativeContext, insightsLoading, scoresLoading, usingLiveInsights, loadAiSummary]);

  useEffect(() => {
    if (!setAdminAssistantContext) return;
    setAdminAssistantContext({
      page: "insights",
      surface: embedded ? "portfolio" : "admin",
      currentRoute: embedded ? "/portfolio?lens=performance" : "/legacy/insights",
      pagePath: embedded ? "/portfolio?lens=performance" : "/legacy/insights",
      activeLens: embedded ? "performance" : null,
      focusOrgId: focusOrgId || null,
      ...narrativeContext,
      companies: analysisOrgs.slice(0, 100),
      workspaceWeeklyTrend: weeklyTrend,
    });
  }, [
    embedded,
    focusOrgId,
    setAdminAssistantContext,
    narrativeContext,
    analysisOrgs,
    weeklyTrend,
  ]);

  const handleExportPdf = useCallback(async () => {
    const layoutRoot = pdfLayoutRootRef.current;
    if (!layoutRoot || pdfExportBusyRef.current) return;
    if (insightsLoading || scoresLoading) {
      toast.error("Wait for insights data to finish loading.");
      return;
    }
    if (!narrative?.trim()) {
      toast.error("Wait for the AI summary to finish loading.");
      return;
    }
    pdfExportBusyRef.current = true;
    setExportingPdf(true);
    const toastId = toast.loading("Generating portfolio roll-up report…");
    const previewWin = openReportLoadingTab("Generating portfolio roll-up report…");
    const captureScale = PDF_CAPTURE_SCALE();
    try {
      if (comparePickerOpen) {
        cancelComparePicker();
        await new Promise((r) => setTimeout(r, 220));
      } else {
        await new Promise((r) => requestAnimationFrame(r));
      }
      const performancePillarSection = layoutRoot.querySelector(
        '[data-pdf-chart-section="performance-pillar"]'
      );
      const participationSection = layoutRoot.querySelector('[data-pdf-chart-section="participation"]');
      const [performancePillarCap, participationCap] = await Promise.all([
        capturePdfChartSection(performancePillarSection, { scale: captureScale, Highcharts }),
        capturePdfChartSection(participationSection, { scale: captureScale, Highcharts }),
      ]);
      const generatedDate = new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const chartsPage1 = performancePillarCap ? [performancePillarCap] : [];
      const chartsPage2 = participationCap ? [participationCap] : [];
      const blob = await exportPortfolioRollupPdf({
        narrative,
        narrative_source: narrativeSource,
        generated_date: generatedDate,
        scope: scopeLine,
        kpi_items: buildInsightsKpiItems(kpis),
        kpis,
        focus_org: focusOrg
          ? {
              name: focusOrg.name,
              industry: focusOrg.industry,
              companySize: focusOrg.companySize,
              overall: focusOrg.overall,
              invited: focusOrg.invited,
              completed: focusOrg.completed,
            }
          : null,
        charts_page1: chartsPage1,
        charts_page2: chartsPage2,
      });
      const url = URL.createObjectURL(blob);
      if (previewWin) {
        previewWin.location.href = url;
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Portfolio report ready", { id: toastId });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e?.message || "Could not create PDF. Try closing the compare picker and retry.", {
        id: toastId,
      });
    } finally {
      pdfExportBusyRef.current = false;
      setExportingPdf(false);
    }
  }, [
    comparePickerOpen,
    cancelComparePicker,
    narrative,
    narrativeSource,
    insightsLoading,
    narrativeLoading,
    scopeLine,
    kpis,
    focusOrg,
  ]);

  useEffect(() => {
    if (urlCompareAppliedRef.current) return;
    const compareParam = searchParams.get("compare");
    if (!compareParam) return;
    if (insightsLoading || scoresLoading) return;
    if (!organisations.length) return;

    const names = compareParam
      .split(",")
      .map((s) => decodeURIComponent(s.trim()))
      .filter(Boolean);
    if (names.length < 2) {
      urlCompareAppliedRef.current = true;
      pendingPortfolioExportRef.current = false;
      toast.error("Select at least 2 companies for a portfolio report.");
      return;
    }

    const wantNames = new Set(names.map((n) => n.toLowerCase()));
    const matched = organisations.filter((o) =>
      wantNames.has(String(o.name || "").trim().toLowerCase())
    );
    const ids = matched.map((o) => o.id).slice(0, MAX_COMPARE_ORGS);

    if (ids.length < 2) {
      urlCompareAppliedRef.current = true;
      pendingPortfolioExportRef.current = false;
      const foundLower = new Set(matched.map((o) => String(o.name || "").trim().toLowerCase()));
      const missing = names.filter((n) => !foundLower.has(n.toLowerCase()));
      toast.error(
        missing.length
          ? `Companies not found: ${missing.join(", ")}`
          : "Could not match at least 2 companies for portfolio report."
      );
    } else {
      setFocusOrgId("");
      setCompareIds(ids);
    }

    urlCompareAppliedRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete("compare");
    next.delete("exportPortfolio");
    setSearchParams(next, { replace: true });
  }, [organisations, insightsLoading, scoresLoading, searchParams, setSearchParams]);

  useEffect(() => {
    if (!pendingPortfolioExportRef.current) return;
    if (insightsLoading || scoresLoading || narrativeLoading) return;
    if (!narrative?.trim()) return;
    if (compareIds.length < 2) return;
    if (pdfExportBusyRef.current) return;

    pendingPortfolioExportRef.current = false;
    handleExportPdf();
  }, [
    insightsLoading,
    scoresLoading,
    narrativeLoading,
    narrative,
    compareIds.length,
    handleExportPdf,
  ]);

  return (
    <div className={embedded ? "nexus-surface-embed" : "min-h-full w-full bg-slate-50/60"}>
      <div className={embedded ? "w-full" : "w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-6 pb-14"}>
        <div ref={pdfLayoutRootRef} className="min-w-0 w-full">
        {!embedded ? (
        <>
        {/* ── Page header ── */}
        <header className="mb-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 min-w-0">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand/60 mb-2">
              Admin · Insights
            </p>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight break-words leading-tight">
              Insights
            </h1>
            {/* Status pill */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {insightsLoading ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 animate-pulse">
                  Loading…
                </span>
              ) : scoresLoading ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200 animate-pulse">
                  Computing scores…
                </span>
              ) : usingLiveInsights ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <span className="relative inline-flex h-1.5 w-1.5 shrink-0" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                  Live
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                  {insightsLoadHint ? "Not loaded" : "Unavailable"}
                </span>
              )}
              <span className="text-xs text-slate-500">
                <span className="font-semibold text-brand">{scopeLine.label}:</span>{" "}
                <span className="text-slate-700">{scopeLine.text}</span>
              </span>
            </div>
            {structureMismatch ? (
              <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-3xl">
                Assessment structure mismatch — companies in this view use different
                assessment formats. Filter by company (or compare like-with-like) so
                section charts stay meaningful.
              </p>
            ) : null}
          </div>
          <div
            className="flex flex-wrap items-center gap-2 shrink-0 pt-1"
            data-export-ignore="true"
          >
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 rounded-lg transition-colors shadow-sm"
            >
              Reset filters
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exportingPdf || insightsLoading || scoresLoading || narrativeLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brand hover:bg-brand-light rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:pointer-events-none active:scale-[0.98]"
            >
              <DownloadIcon className="w-4 h-4" />
              {exportingPdf ? "Generating…" : "Generate report"}
            </button>
          </div>
        </header>
        </>
        ) : (
        <header className="mb-4 flex flex-wrap items-center justify-end gap-2 min-w-0" data-export-ignore="true">
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 rounded-lg transition-colors shadow-sm"
          >
            Reset filters
          </button>
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exportingPdf || insightsLoading || scoresLoading || narrativeLoading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brand hover:bg-brand-light rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:pointer-events-none active:scale-[0.98]"
          >
            <DownloadIcon className="w-4 h-4" />
            {exportingPdf ? "Generating…" : "Generate report"}
          </button>
        </header>
        )}

        {/* ── Context chips / alerts ── */}
        {(insightsLoadHint || focusOrgId || compareIds.length > 0) && (
          <div className="flex flex-wrap gap-2 mb-5" data-export-ignore>
            {insightsLoadHint && (
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-full"
                role="alert"
              >
                <span className="font-bold">⚠</span> {insightsLoadHint}
              </div>
            )}
            {focusOrgId && focusOrg && (
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-brand bg-brand/8 border border-brand/20 rounded-full"
                role="status"
              >
                Focus: {focusOrg.name}
                <button
                  type="button"
                  onClick={() => setFocusOrgId("")}
                  className="ml-0.5 text-brand/60 hover:text-brand"
                  aria-label="Clear focus"
                >
                  <XSmallIcon />
                </button>
              </div>
            )}
            {compareIds.length > 0 && !focusOrgId && (
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-full"
                role="status"
              >
                Compare: {compareIds.length} {compareIds.length === 1 ? "company" : "companies"}
                <button
                  type="button"
                  onClick={() => setCompareIds([])}
                  className="ml-0.5 text-violet-400 hover:text-violet-700"
                  aria-label="Clear compare"
                >
                  <XSmallIcon />
                </button>
              </div>
            )}
          </div>
        )}

        <InsightsNarrativePanel
          narrative={narrative}
          narrativeLoading={narrativeLoading}
          narrativeSource={narrativeSource}
          onGenerate={loadAiSummary}
          dataReady={!insightsLoading && !scoresLoading && usingLiveInsights}
        />

        <BoardBriefCard
          context={narrativeContext}
          companies={analysisOrgs}
          anomalies={anomalies}
          dataReady={!insightsLoading && !scoresLoading && usingLiveInsights}
        />


        {/* ── Filters — omitted from PDF ── */}
        <div
          className={`admin-chart-card rounded-2xl border border-slate-200 bg-white mb-5 sm:mb-6 shadow-sm min-w-0 max-w-full ${
            comparePickerOpen ? "relative z-[100]" : ""
          }`}
          data-pdf-remove-on-page1
        >
          {/* Collapsible header */}
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50/80 transition-colors"
            aria-expanded={filtersOpen}
          >
            <div className="flex items-center gap-2">
              <FilterIcon className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-700">Filters</span>
              {(industry !== FILTER_ALL_INDUSTRIES || companySize !== FILTER_ALL_COMPANY_SIZES || dateFrom || dateTo || compareIds.length > 0) && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-brand/10 text-brand">
                  Active
                </span>
              )}
            </div>
            <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`} />
          </button>

          {filtersOpen && (
            <div className="px-4 pb-4 border-t border-slate-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 w-full min-w-0 pt-4">
                {/* Companies compare picker */}
                <div
                  ref={comparePickerRef}
                  className={`md:col-span-2 lg:col-span-2 xl:col-span-2 min-w-0 relative ${
                    comparePickerOpen ? "z-[110]" : "z-20"
                  }`}
                >
                  <label
                    className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5"
                    id="compare-org-label"
                  >
                    Companies (compare) — {compareIds.length}/{MAX_COMPARE_ORGS}
                  </label>
                  <button
                    type="button"
                    id="compare-org-trigger"
                    aria-haspopup="listbox"
                    aria-expanded={comparePickerOpen}
                    aria-label="Open company picker for compare"
                    onClick={() =>
                      comparePickerOpen ? cancelComparePicker() : openComparePicker()
                    }
                    className={`insights-compare-trigger ${selectClass} text-left flex items-center justify-between gap-2`}
                  >
                    <span className="truncate">{compareTriggerSummary}</span>
                    <ChevronDownIcon className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${comparePickerOpen ? "rotate-180" : ""}`} />
                  </button>

                  {comparePickerOpen && (
                    <div
                      className="insights-compare-picker absolute left-0 right-0 mt-1.5 rounded-xl border border-slate-200 bg-white shadow-xl z-[120] flex flex-col max-h-[min(22rem,55vh)] overflow-hidden"
                      role="dialog"
                      aria-label="Select companies for compare"
                    >
                      <div className="insights-compare-picker-head px-3 py-2.5 border-b border-slate-100 text-xs text-slate-500 bg-slate-50/80">
                        Select up to <strong className="text-slate-700">{MAX_COMPARE_ORGS}</strong> companies, then{" "}
                        <strong className="text-slate-700">Apply</strong>.
                      </div>
                      <ul
                        className="overflow-y-auto overscroll-contain py-1 px-1.5 min-h-0 flex-1"
                        role="listbox"
                        aria-multiselectable="true"
                      >
                        {masterTableRows.length === 0 ? (
                          <li className="px-2 py-4 text-sm text-slate-400 text-center">
                            No companies match filters.
                          </li>
                        ) : (
                          masterTableRows.map((o) => {
                            const checked = compareDraftIds.includes(o.id);
                            const atCap = !checked && compareDraftIds.length >= MAX_COMPARE_ORGS;
                            return (
                              <li key={o.id} role="option" aria-selected={checked}>
                                <label className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-slate-50 text-sm transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={atCap}
                                    onChange={() => toggleCompareDraft(o.id)}
                                    className="rounded border-slate-300 text-brand focus:ring-brand/30"
                                  />
                                  <span className={atCap && !checked ? "text-slate-300" : "text-slate-800"}>
                                    {o.name}
                                  </span>
                                </label>
                              </li>
                            );
                          })
                        )}
                      </ul>
                      <div className="insights-compare-picker-foot flex justify-end gap-2 px-3 py-2.5 border-t border-slate-100 bg-slate-50/80 rounded-b-xl">
                        <button
                          type="button"
                          onClick={cancelComparePicker}
                          className="text-sm font-medium text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-200/80 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={applyComparePicker}
                          className="text-sm font-semibold text-white bg-brand px-4 py-1.5 rounded-lg hover:bg-brand-light transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}

                  {focusOrgId ? (
                    <p className="text-[11px] text-amber-700 mt-1.5 leading-tight">
                      Row focus is active — clear focus first to use compare.
                    </p>
                  ) : null}
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                    Industry
                  </label>
                  <div className="relative">
                    <select
                      className={selectClass}
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                    >
                      {industryFilterOptions.map((i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  </div>
                </div>

                {/* Company size */}
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                    Company size
                  </label>
                  <div className="relative">
                    <select
                      className={selectClass}
                      value={companySize}
                      onChange={(e) => setCompanySize(e.target.value)}
                    >
                      {companySizeFilterOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  </div>
                </div>

                {/* Date range */}
                <div className="md:col-span-2 xl:col-span-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                    Date range
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      className={`${selectClass} flex-1`}
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      placeholder="From"
                    />
                    <span className="text-slate-400 text-sm shrink-0">–</span>
                    <input
                      type="date"
                      className={`${selectClass} flex-1`}
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      placeholder="To"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Global KPIs ── */}
        <SectionHeader title="Overview" />
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4 mb-6 w-full min-w-0 relative z-0">
          <KpiCard
            metaKey="totalOrgs"
            label="Total companies"
            value={kpis.totalOrgs}
            loading={insightsLoading}
          />
          <KpiCard
            metaKey="participantsInvited"
            label="Participants invited"
            value={kpis.participantsInvited}
            loading={insightsLoading}
          />
          <KpiCard
            metaKey="participantsCompleted"
            label="Full assessments completed"
            value={kpis.participantsCompleted}
            loading={insightsLoading}
          />
          <KpiCard
            metaKey="participantsLiteCompleted"
            label="Lite assessments completed"
            value={kpis.participantsLiteCompleted}
            loading={insightsLoading}
          />
          <KpiCard
            metaKey="avgCompletion"
            label="Avg completion %"
            value={kpis.avgCompletion != null ? `${kpis.avgCompletion}%` : "—"}
            loading={insightsLoading}
          />
          <KpiCard
            metaKey="avgOverall"
            label="Avg overall score"
            value={kpis.avgOverall != null ? kpis.avgOverall : "—"}
            loading={insightsLoading || scoresLoading}
          />
          <KpiCard
            metaKey="activeAssessments"
            label="Active assessments"
            value={kpis.activeAssessments}
            loading={insightsLoading}
          />
          <KpiCard
            metaKey="reportReadyCount"
            label="Report-ready companies"
            value={kpis.reportReadyCount}
            loading={insightsLoading || scoresLoading}
          />
        </div>

        {/* ── Focus company strip ── */}
        {focusOrg && (
          <div className="mb-5 sm:mb-6 rounded-2xl border border-brand/25 bg-gradient-to-r from-brand/5 to-transparent p-4 sm:p-5 min-w-0">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-brand/15 flex items-center justify-center text-brand font-bold text-sm shrink-0">
                  {focusOrg.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-brand/60 mb-0.5">
                    Focused company
                  </div>
                  <div className="text-base sm:text-lg font-bold text-slate-900 break-words leading-tight">
                    {focusOrg.name}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {[focusOrg.industry, focusOrg.companySize].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="text-center px-3 py-1.5 rounded-xl bg-white border border-slate-200 shadow-sm">
                  <div className="text-lg font-bold text-slate-900 tabular-nums">
                    {focusOrg.invited ? Math.round((focusOrg.completed / focusOrg.invited) * 100) : 0}%
                  </div>
                  <div className="text-[10px] text-slate-500 font-medium">Completion</div>
                </div>
                <div className="text-center px-3 py-1.5 rounded-xl bg-white border border-slate-200 shadow-sm">
                  <div className={`text-lg font-bold tabular-nums ${focusOrg.reportReady ? "text-emerald-600" : "text-slate-400"}`}>
                    {focusOrg.reportReady ? "Ready" : "Not yet"}
                  </div>
                  <div className="text-[10px] text-slate-500 font-medium">Report</div>
                </div>
                {focusVsMedian && (
                  <div className="text-center px-3 py-1.5 rounded-xl bg-white border border-slate-200 shadow-sm">
                    <div className={`text-lg font-bold tabular-nums ${focusVsMedian.delta >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                      {focusVsMedian.delta >= 0 ? "+" : ""}{focusVsMedian.delta}
                    </div>
                    <div className="text-[10px] text-slate-500 font-medium">vs median ({focusVsMedian.cohort})</div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setFocusOrgId("")}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-white/80 border border-transparent hover:border-slate-200 transition-colors"
                >
                  Clear focus
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Performance + Pillar — single PDF chart page ── */}
        <div data-pdf-chart-section="performance-pillar" className="w-full min-w-0 mb-6">
        <div className="mb-6 w-full min-w-0">
        <SectionHeader title="Performance" subtitle="Ranking & score distribution" />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 w-full min-w-0">
          <div
            className="admin-chart-card relative rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm min-w-0 max-w-full hover:shadow-md transition-shadow"
          >
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Performance ranking</h3>
            <ChartPanel scrollY>
              <AdminHighcharts
                highcharts={Highcharts}
                options={percentileOptions}
                containerProps={hcContainerProps}
              />
            </ChartPanel>
            <ChartExplainAction
              chartId="performance_ranking"
              onExplain={handleExplainChart}
              explanation={chartExplainById.performance_ranking?.explanation}
              source={chartExplainById.performance_ranking?.source}
              loading={Boolean(chartExplainLoadingById.performance_ranking)}
            />
          </div>
          <div
            className="admin-chart-card relative rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm min-w-0 max-w-full hover:shadow-md transition-shadow"
          >
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Score band distribution</h3>
            <ChartPanel>
              <AdminHighcharts
                highcharts={Highcharts}
                options={histogramOptions}
                containerProps={hcContainerProps}
              />
            </ChartPanel>
            <ChartExplainAction
              chartId="score_band"
              onExplain={handleExplainChart}
              explanation={chartExplainById.score_band?.explanation}
              source={chartExplainById.score_band?.source}
              loading={Boolean(chartExplainLoadingById.score_band)}
            />
          </div>
        </div>
        </div>

          <div className="w-full min-w-0">
          <SectionHeader title="Pillar Analysis" subtitle="Profile · Importance · Agreement" />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 w-full min-w-0">
            <div
              className="admin-chart-card relative rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm min-w-0 max-w-full hover:shadow-md transition-shadow"
            >
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Pillar profile</h3>
              <ChartPanel>
                <AdminHighcharts
                  highcharts={Highcharts}
                  options={pillarCohortOptions}
                  containerProps={hcContainerProps}
                />
              </ChartPanel>
              <ChartExplainAction
                chartId="pillar_profile"
                onExplain={handleExplainChart}
                explanation={chartExplainById.pillar_profile?.explanation}
                source={chartExplainById.pillar_profile?.source}
                loading={Boolean(chartExplainLoadingById.pillar_profile)}
              />
            </div>
            <div
              className="admin-chart-card relative rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm min-w-0 max-w-full hover:shadow-md transition-shadow"
            >
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Importance scores</h3>
              <ChartPanel>
                <AdminHighcharts
                  highcharts={Highcharts}
                  options={pillarImportanceCohortOptions}
                  containerProps={hcContainerProps}
                />
              </ChartPanel>
              <ChartExplainAction
                chartId="importance_scores"
                onExplain={handleExplainChart}
                explanation={chartExplainById.importance_scores?.explanation}
                source={chartExplainById.importance_scores?.source}
                loading={Boolean(chartExplainLoadingById.importance_scores)}
              />
            </div>
            <div
              className="admin-chart-card relative rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm min-w-0 max-w-full hover:shadow-md transition-shadow"
            >
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Agreement scores</h3>
              <ChartPanel>
                <AdminHighcharts
                  highcharts={Highcharts}
                  options={pillarAgreementCohortOptions}
                  containerProps={hcContainerProps}
                />
              </ChartPanel>
              <ChartExplainAction
                chartId="agreement_scores"
                onExplain={handleExplainChart}
                explanation={chartExplainById.agreement_scores?.explanation}
                source={chartExplainById.agreement_scores?.source}
                loading={Boolean(chartExplainLoadingById.agreement_scores)}
              />
            </div>
          </div>
        </div>
        </div>

        <div data-pdf-chart-section="participation" className="mb-6 w-full min-w-0">
          <SectionHeader title="Participation" subtitle="By company · Weekly trend" />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 w-full min-w-0">
            <div
              className="admin-chart-card relative rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm min-w-0 max-w-full hover:shadow-md transition-shadow"
            >
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Assessment participation</h3>
              <ChartPanel scrollY>
                <AdminHighcharts
                  key={participationChartRemountKey}
                  highcharts={Highcharts}
                  options={participationByOrgOptions}
                  containerProps={hcContainerProps}
                />
              </ChartPanel>
              <ChartExplainAction
                chartId="participation"
                onExplain={handleExplainChart}
                explanation={chartExplainById.participation?.explanation}
                source={chartExplainById.participation?.source}
                loading={Boolean(chartExplainLoadingById.participation)}
              />
            </div>
            <div
              className="admin-chart-card relative rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm min-w-0 max-w-full hover:shadow-md transition-shadow"
            >
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Weekly completions</h3>
              <ChartPanel scrollY>
                <AdminHighcharts
                  key={weeklyChartRemountKey}
                  highcharts={Highcharts}
                  options={trendOptions}
                  containerProps={hcContainerProps}
                />
              </ChartPanel>
              <ChartExplainAction
                chartId="weekly_completions"
                onExplain={handleExplainChart}
                explanation={chartExplainById.weekly_completions?.explanation}
                source={chartExplainById.weekly_completions?.source}
                loading={Boolean(chartExplainLoadingById.weekly_completions)}
              />
            </div>
          </div>
        </div>

        {/* ── Master table — PDF omitted ── */}
        <div
          className="admin-chart-card rounded-2xl border border-slate-200/80 bg-white shadow-sm min-w-0 max-w-full overflow-hidden"
          data-pdf-remove-on-page1
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 pt-4 pb-3 border-b border-slate-100 min-w-0">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Company directory</h2>
              <p className="text-xs text-slate-400 mt-0.5">Click a row to focus · tick to compare</p>
            </div>
            <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-3 py-1 shrink-0">
              {filteredOrgs.length} {filteredOrgs.length === 1 ? "company" : "companies"} · {compareIds.length} in compare
            </span>
          </div>
          <div className="overflow-x-auto w-full min-w-0">
            <DataTable
              columns={tableColumns}
              data={masterTableRows}
              keyField="id"
              highlightOnHover
              pagination
              paginationPerPage={10}
              onRowClicked={(row) =>
                setFocusOrgId((prev) => (prev === row.id ? "" : row.id))
              }
              conditionalRowStyles={conditionalRowStyles}
              customStyles={masterTableCustomStyles}
            />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default InsightsPage;
